import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { LottiePlayer } from "@/components/ui/lottie-player";
import { AppleButton } from "@/components/ui/apple-button";
import { ArrowRight, ArrowLeft } from "lucide-react";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");

  // Get the callback URL based on environment
  const getCallbackUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost') {
      return 'http://localhost:3000/auth/callback';
    }
    // Use the same hostname that the user is currently on
    return `https://${hostname}/auth/callback`;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        navigate("/scan");
      }
      if (event === "USER_UPDATED") {
        setErrorMessage("");
      }
      if (event === "SIGNED_OUT") {
        setErrorMessage("");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.status) {
        case 400:
          return t('auth.errors.invalidCredentials');
        case 422:
          return t('auth.errors.invalidEmail');
        default:
          return error.message;
      }
    }
    return error.message;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-between items-center -ml-3">
            <AppleButton 
              variant="secondary" 
              onClick={() => navigate("/")}
              className="hover:bg-transparent text-gray-600"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              {t('buttons.back')}
            </AppleButton>
            <LanguageSwitcher />
          </div>
          <div className="w-[130px] h-[130px] mx-auto">
            <LottiePlayer
              src="https://lottie.host/a5bba61b-ca65-466e-885a-467856ae4456/VPzIzTB6RK.lottie"
              style={{ width: '100%', height: '100%' }}
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth.welcome')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('auth.signInMessage')}
          </p>
          <div className="mt-4 flex justify-center">
            <AppleButton onClick={() => navigate("/public/scan")} variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-200">
              {t('buttons.publicScan')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </AppleButton>
          </div>
        </div>
        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {errorMessage && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          <SupabaseAuth 
            supabaseClient={supabase}
            appearance={{ 
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: '#2563eb',
                    brandAccent: '#1d4ed8',
                  },
                },
              },
              style: {
                button: {
                  borderRadius: '0.5rem',
                  height: '2.75rem',
                },
                container: {
                  gap: '1rem',
                },
                divider: {
                  margin: '1.5rem 0',
                },
              },
            }}
            providers={['google']}
            redirectTo={getCallbackUrl()}
          />
        </div>
      </div>
    </div>
  );
};

export default Auth;